import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, finalize, merge, Observable, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Inject, Injectable } from '@angular/core';
import {
	LibraryCollectionItem,
	LibraryCollectionItemsAndFolders,
	LibraryFolder
} from '@go-modules/interfaces/library/library-collection';
import { LibraryService } from '@go-modules/services/library/library.service';
import {
	LibraryTreeNode,
	NodeKind,
	NodeType
} from '@go-modules/components/library/library-tree-viewer/library-tree-node';
import {
	LibraryCollectionItemCacheService
} from '@go-modules/services/library-collection-item-cache/library-collection-item-cache.service';
import {
	CONTENT_TYPES
} from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';

interface FolderCache {
	children: LibraryTreeNode[];
}

type SortField = 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at' | 'modified_at' | '-modified_at';

@Injectable()
export class LibraryTreeDataSource extends DataSource<LibraryTreeNode> {
	private readonly dataChange = new BehaviorSubject<LibraryTreeNode[]>([]);
	public readonly data$ = this.dataChange.asObservable();
	private readonly disconnect$ = new Subject<void>();
	private collectionItems: LibraryCollectionItemsAndFolders | null = null;
	private collectionId: number | null = null;
	private contentType = CONTENT_TYPES.ALL;
	private userIds: number[] = [];
	private sortBy: SortField = 'name';
	private currentSearch: string = '';

	public expandedNodes = new Set<string>();
	public loadingSubscriptions = new Map<string, Subscription>();
	public folderCache = new Map<string, FolderCache>();
	public foldersNeedingRefresh = new Set<string>();
	public loadingItemDetails = new Set<string>();

	public readonly levelAccessor = (node: LibraryTreeNode) => node.level;

	public getCollectionId () {
		return this.collectionId;
	}

	public setSortBy (sortBy: string): void {
		this.sortBy = sortBy as SortField;
		for (const [, cache] of this.folderCache.entries()) {
			cache.children = this.sortNodes(cache.children);
		}
		this.resortTree();
	}

	public setSearch (searchTerm: string): void {
		const trimmed = searchTerm?.trim() || '';
		const wasSearching = this.currentSearch !== '';
		const isNowSearching = trimmed !== '';

		// Clear caches when search state changes
		if (wasSearching !== isNowSearching || this.currentSearch !== trimmed) {
			this.folderCache.clear();
			this.foldersNeedingRefresh.clear();

			// Only clear expanded nodes when ending search
			if (wasSearching && !isNowSearching) {
				this.expandedNodes.clear();
			}
		}

		this.currentSearch = trimmed;
	}

	public isSearchActive (): boolean {
		return this.currentSearch !== '';
	}

	public getParentId (node: LibraryTreeNode): number | null {
		if (node.type === NodeType.Folder) {
			return (node.data as LibraryFolder).parent_id;
		} else if (node.type === NodeType.Item) {
			return (node.data as LibraryCollectionItem).library_collection_folder_id;
		}
		return null;
	}

	public get data (): LibraryTreeNode[] {
		return this.dataChange.value;
	}

	public set data (value: LibraryTreeNode[]) {
		this.dataChange.next(value);
	}

	public constructor (
		@Inject(LibraryService) private libraryService: LibraryService,
		@Inject(LibraryCollectionItemCacheService) private itemCacheService: LibraryCollectionItemCacheService
	) {
		super();
	}

	public getUserIds (): number[] {
		return this.userIds;
	}

	public connect (collectionViewer: CollectionViewer): Observable<LibraryTreeNode[]> {
		return merge(collectionViewer.viewChange, this.dataChange).pipe(
			map(() => this.data)
		);
	}

	public disconnect (): void {
		this.loadingSubscriptions.forEach((subscription) => subscription.unsubscribe());
		this.loadingSubscriptions.clear();
		this.disconnect$.next(); // do not complete to allow reconnections
	}

	/**
	 * Build initial tree structure or update existing one
	 * When preserveState is true, maintains expanded folders without refetching
	 */
	public setCollectionItems (
		items: LibraryCollectionItemsAndFolders,
		collectionId: number,
		contentType: CONTENT_TYPES,
		preserveState = true
	): void {
		// Clear everything if switching collections or content type
		if (!preserveState || this.collectionId !== collectionId || this.contentType !== contentType) {
			const savedSearch = this.currentSearch;
			this.clear();
			this.currentSearch = savedSearch; // restore

			this.collectionItems = items;
			this.collectionId = collectionId;
			this.contentType = contentType;
			this.userIds = items.user_ids;
			this.buildTreeRoot();
			return;
		}

		const previouslyExpanded = new Set(this.expandedNodes);
		this.collectionItems = items;
		this.accumulateUserIds(items.user_ids);

		this.rebuildTreePreservingCache(previouslyExpanded);
	}

	private accumulateUserIds (newUserIds: number[]): void {
		if (!newUserIds || newUserIds.length === 0) return;

		const userIdSet = new Set(this.userIds);
		newUserIds.forEach((id) => userIdSet.add(id));
		this.userIds = Array.from(userIdSet);
	}

	/**
	 * Rebuild tree using cached data for expanded folders
	 */
	private rebuildTreePreservingCache (expandedNodeKeys: Set<string>): void {
		const newData: LibraryTreeNode[] = [];
		this.expandedNodes = new Set(expandedNodeKeys);

		const existingFolderIds = new Set<number>();
		this.collectionItems.folders.forEach((f) => existingFolderIds.add(f.id));

		// Build root level - include orphan folders
		const rootFolders = this.collectionItems.folders.filter((f) => {
			if (!f.parent_id) return true;
			return !existingFolderIds.has(f.parent_id);
		});

		const rootItems = this.collectionItems.items.filter((item) =>
			item.library_collection_folder_id === null
		);

		// Create and sort root level nodes
		const rootFolderNodes = rootFolders.map((folder) =>
			new LibraryTreeNode(folder, NodeType.Folder, 0, true)
		);

		const rootItemNodes = rootItems.map((item) => {
			const cachedItem = this.itemCacheService.getItem(item.hash);
			const itemToUse = cachedItem || item;
			const itemNode = new LibraryTreeNode(itemToUse, NodeType.Item, 0, false);
			itemNode.checked = itemToUse.checked || false;
			return itemNode;
		});

		const sortedRootNodes = this.sortNodes([...rootFolderNodes, ...rootItemNodes]);

		// Add sorted root nodes and expand cached children
		sortedRootNodes.forEach((node) => {
			newData.push(node);

			if (node.type === NodeType.Folder && expandedNodeKeys.has(node.key)) {
				this.insertCachedChildren(newData, node, expandedNodeKeys);
			}
		});

		// Clear any folders marked for refresh that are no longer expanded
		for (const key of this.foldersNeedingRefresh) {
			if (!expandedNodeKeys.has(key)) {
				this.foldersNeedingRefresh.delete(key);
			}
		}

		this.data = newData;
	}

	/**
	 * Insert cached children for an expanded folder
	 */
	private insertCachedChildren (
		targetArray: LibraryTreeNode[],
		parentNode: LibraryTreeNode,
		expandedNodeKeys: Set<string>
	): void {
		const cached = this.folderCache.get(parentNode.key);
		if (!cached) return;

		// Insert each cached child
		cached.children.forEach((childNode) => {
			if (childNode.type === NodeType.Item) {
				const item = childNode.data as LibraryCollectionItem;
				const freshCache = this.itemCacheService.getItem(item.hash);
				if (freshCache) {
					childNode.data = freshCache;
				}
			}

			targetArray.push(childNode);

			// If child is an expanded folder, recursively add its cached children
			if (childNode.type === NodeType.Folder && expandedNodeKeys.has(childNode.key)) {
				this.expandedNodes.add(childNode.key);
				this.insertCachedChildren(targetArray, childNode, expandedNodeKeys);
			}
		});
	}

	public findInsertionIndex (
		startIndex: number,
		targetLevel: number,
		nodeType: NodeKind,
		nodeToInsert?: LibraryTreeNode | LibraryFolder,
		parentLevel?: number
	): number {
		// Convert raw folder to tree node for comparison
		let nodeToInsertAsTreeNode: LibraryTreeNode | null = null;
		if (nodeToInsert) {
			if (nodeToInsert instanceof LibraryTreeNode) {
				nodeToInsertAsTreeNode = nodeToInsert;
			} else {
				nodeToInsertAsTreeNode = new LibraryTreeNode(nodeToInsert, NodeType.Folder, targetLevel, true);
			}
		}

		// For child insertion
		if (parentLevel !== undefined) {
			for (let i = startIndex; i < this.data.length; i++) {
				const node = this.data[i];

				// Stop if we've gone past children
				if (node.level <= parentLevel) {
					return i;
				}

				// Skip grandchildren
				if (node.level > targetLevel) {
					continue;
				}

				// Folders before items
				if (nodeType === NodeType.Folder && node.type === NodeType.Item) {
					return i;
				}

				// Items after all folders at same level
				if (nodeToInsertAsTreeNode && node.type === nodeType) {
					if (this.compareNodes(nodeToInsertAsTreeNode, node) < 0) {
						return i;
					}
				}
			}
			return this.data.length;
		}

		// For root level insertion
		let insertPosition = this.data.length;

		for (let i = startIndex; i < this.data.length; i++) {
			const node = this.data[i];

			// Skip nodes at different levels
			if (node.level !== targetLevel) continue;

			// For folder insertion
			if (nodeType === NodeType.Folder) {
				// Insert before first item at this level
				if (node.type === NodeType.Item) {
					return i;
				}

				// Sort-based ordering among folders
				if (node.type === NodeType.Folder && nodeToInsertAsTreeNode) {
					if (this.compareNodes(nodeToInsertAsTreeNode, node) < 0) {
						return i;
					}
				}
			}

			// For item insertion
			if (nodeType === NodeType.Item) {
				// Update position to be after all folders at this level
				if (node.type === NodeType.Folder) {
					insertPosition = i + 1;
				}
				// Sort-based ordering for items
				else if (node.type === NodeType.Item && nodeToInsertAsTreeNode) {
					if (this.compareNodes(nodeToInsertAsTreeNode, node) < 0) {
						return i;
					}
				}
			}
		}

		return insertPosition;
	}

	/**
	 * Add a new folder to the tree
	 */
	public addFolder (folder: LibraryFolder, parentId: number | null = null): void {
		const parentKey = parentId ? `folder_${parentId}` : null;

		if (!parentId) {
			// Add to root level
			const folderNode = new LibraryTreeNode(folder, NodeType.Folder, 0, true);
			const insertIndex = this.findInsertionIndex(0, 0, NodeType.Folder, folder);
			this.data.splice(insertIndex, 0, folderNode);
		} else {
			// Mark parent folder cache as stale
			if (parentKey) {
				this.foldersNeedingRefresh.add(parentKey);
			}

			// If parent is expanded, update the view
			const { node: parentNode, index: parentIndex } = this.findNodeWithIndex(parentKey);
			if (parentNode && parentIndex >= 0 && this.expandedNodes.has(parentKey)) {
				const folderNode = new LibraryTreeNode(folder, NodeType.Folder, parentNode.level + 1, true);

				const insertIndex = this.findInsertionIndex(
					parentIndex + 1,
					parentNode.level + 1,
					NodeType.Folder,
					folderNode,
					parentNode.level
				);
				this.data.splice(insertIndex, 0, folderNode);

				// Update cache to include new folder
				this.updateCacheWithNewNode(parentKey, folderNode);
			}
		}

		this.dataChange.next(this.data);
	}

	/**
	 * Add a new item to the tree
	 */
	public addItem (item: LibraryCollectionItem, folderId: number | null = null): void {
		const folderKey = folderId ? `folder_${folderId}` : null;

		if (!folderId) {
			// Add to root level
			const itemNode = new LibraryTreeNode(item, NodeType.Item, 0, false);
			itemNode.checked = item.checked || false;
			const insertIndex = this.findInsertionIndex(0, 0, NodeType.Item, itemNode);
			this.data.splice(insertIndex, 0, itemNode);
		} else {
			// Mark folder cache as stale
			if (folderKey) {
				this.foldersNeedingRefresh.add(folderKey);
			}

			// If folder is expanded, update the view
			const { node: folderNode, index: folderIndex } = this.findNodeWithIndex(folderKey);
			if (folderNode && folderIndex >= 0 && this.expandedNodes.has(folderKey)) {
				const itemNode = new LibraryTreeNode(item, NodeType.Item, folderNode.level + 1, false);
				itemNode.checked = item.checked || false;

				const insertIndex = this.findInsertionIndex(
					folderIndex + 1,
					folderNode.level + 1,
					NodeType.Item,
					itemNode,
					folderNode.level
				);
				this.data.splice(insertIndex, 0, itemNode);

				// Update cache to include new item
				this.updateCacheWithNewNode(folderKey, itemNode);
			}
		}

		this.dataChange.next(this.data);
	}

	/**
	 * Move an item between folders
	 */
	public moveItem (itemId: number, fromFolderId: number | null, toFolderId: number | null): void {
		// Find the item in the visible tree
		const itemIndex = this.data.findIndex((n) =>
			n.type === NodeType.Item && (n.data as LibraryCollectionItem).id === itemId
		);

		if (itemIndex < 0) return; // Item not visible, nothing to update

		const itemNode = this.data[itemIndex];
		const item = itemNode.data as LibraryCollectionItem;

		// Update the item's folder reference
		item.library_collection_folder_id = toFolderId;

		// Remove from current position
		this.data.splice(itemIndex, 1);

		// Update source folder cache
		if (fromFolderId != null) {
			const fromKey = `folder_${fromFolderId}`;
			this.foldersNeedingRefresh.add(fromKey);
			this.removeCachedNode(fromKey, itemNode.key);
		}

		// Insert at new position
		if (toFolderId == null) {
			// Moving to root - find insertion point after all root folders
			itemNode.level = 0;
			const insertIndex = this.findInsertionIndex(0, 0, NodeType.Item, itemNode);
			this.data.splice(insertIndex, 0, itemNode);
		} else {
			// Moving to a folder
			const toKey = `folder_${toFolderId}`;
			this.foldersNeedingRefresh.add(toKey);

			const { node: toFolderNode, index: toFolderIndex } = this.findNodeWithIndex(toKey);
			if (toFolderNode && toFolderIndex >= 0 && this.expandedNodes.has(toKey)) {
				itemNode.level = toFolderNode.level + 1;

				const insertIndex = this.findInsertionIndex(
					toFolderIndex + 1,
					toFolderNode.level + 1,
					NodeType.Item,
					itemNode,
					toFolderNode.level
				);
				this.data.splice(insertIndex, 0, itemNode);
				this.updateCacheWithNewNode(toKey, itemNode);
			}
		}

		this.dataChange.next(this.data);
	}

	public removeItemByHash (itemHash: string): void {
		const itemIndex = this.data.findIndex((n) =>
			n.type === NodeType.Item && (n.data as LibraryCollectionItem).hash === itemHash
		);

		if (itemIndex >= 0) {
			const itemNode = this.data[itemIndex];
			const item = itemNode.data as LibraryCollectionItem;

			// Mark folder cache as stale if item was in a folder
			if (item.library_collection_folder_id) {
				const folderKey = `folder_${item.library_collection_folder_id}`;
				this.foldersNeedingRefresh.add(folderKey);
				this.removeCachedNode(folderKey, itemNode.key);
			}

			// Remove from tree
			this.data.splice(itemIndex, 1);
		}
		this.dataChange.next(this.data);
	}

	/**
	 * Remove a folder and its contents from the tree
	 */
	public removeFolder (folderId: number): void {
		const folderKey = `folder_${folderId}`;
		const { node: folderNode, index: folderIndex } = this.findNodeWithIndex(folderKey);

		if (!folderNode || folderIndex < 0) return;

		const folder = folderNode.data as LibraryFolder;

		// Mark parent folder for refresh and remove parent's cache
		if (folder.parent_id) {
			const parentKey = `folder_${folder.parent_id}`;
			this.foldersNeedingRefresh.add(parentKey);
			this.removeCachedNode(parentKey, folderKey);
		}

		// Count descendants to remove
		let count = 1;
		while (folderIndex + count < this.data.length &&
		this.data[folderIndex + count].level > folderNode.level) {
			count++;
		}

		// Remove folder and descendants
		this.data.splice(folderIndex, count);

		// Clean up state
		this.expandedNodes.delete(folderKey);
		this.folderCache.delete(folderKey);
		this.foldersNeedingRefresh.delete(folderKey);

		this.dataChange.next(this.data);
	}

	private findNodeWithIndex (key: string): { node: LibraryTreeNode | undefined; index: number } {
		for (let i = 0; i < this.data.length; i++) {
			if (this.data[i].key === key) {
				return { node: this.data[i], index: i };
			}
		}
		return { node: undefined, index: -1 };
	}

	private updateCacheWithNewNode (parentKey: string, newNode: LibraryTreeNode): void {
		const cached = this.folderCache.get(parentKey);
		if (cached) {
			const insertIndex = this.findInsertionIndexInArray(cached.children, newNode);
			cached.children.splice(insertIndex, 0, newNode);
		}
	}

	private removeCachedNode (parentKey: string, nodeKey: string): void {
		const cached = this.folderCache.get(parentKey);
		if (cached) {
			cached.children = cached.children.filter((n) => n.key !== nodeKey);
		}
	}

	public findInsertionIndexInArray (nodes: LibraryTreeNode[], newNode: LibraryTreeNode): number {
		for (let i = 0; i < nodes.length; i++) {
			if (this.compareNodes(newNode, nodes[i]) < 0) {
				return i;
			}
		}
		return nodes.length;
	}

	private buildTreeRoot (): void {
		if (this.isSearchActive()) {
			this.buildTreeFromSearchResults();
		} else {
			this.buildNormalTree();
		}

		this.collectionItems = null;
	}

	private buildNormalTree (): void {
		const existingFolderIds = new Set<number>();
		this.collectionItems.folders.forEach((f) => existingFolderIds.add(f.id));

		// Include orphan folders at root
		const rootFolders = this.collectionItems.folders.filter((f) => {
			if (!f.parent_id) return true;
			return !existingFolderIds.has(f.parent_id);
		});

		const rootItems = this.collectionItems.items.filter((item) =>
			item.library_collection_folder_id === null
		);

		this.data = this.transformResponseToSortedNodes(rootFolders, rootItems, 0);
	}

	/**
	 * Build search tree showing ALL folders and items returned by the API
	 * Folders containing items or child folders are auto-expanded to show the results.
	 */
	private buildTreeFromSearchResults (): void {
		// quick lookup
		const folderMap = new Map<number, LibraryFolder>();
		this.collectionItems.folders.forEach((folder) => {
			folderMap.set(folder.id, folder);
		});

		// group by parent
		const foldersByParent = new Map<number | null, LibraryFolder[]>();
		const itemsByFolder = new Map<number | null, LibraryCollectionItem[]>();

		this.collectionItems.folders.forEach((folder) => {
			const parentId = folder.parent_id;
			if (!foldersByParent.has(parentId)) {
				foldersByParent.set(parentId, []);
			}
			foldersByParent.get(parentId)!.push(folder);
		});

		this.collectionItems.items.forEach((item) => {
			const folderId = item.library_collection_folder_id;
			if (!itemsByFolder.has(folderId)) {
				itemsByFolder.set(folderId, []);
			}
			itemsByFolder.get(folderId)!.push(item);
		});

		const folderContainsItems = (folderId: number): boolean => {
			// directly contains items
			if (itemsByFolder.has(folderId) && itemsByFolder.get(folderId)!.length > 0) {
				return true;
			}

			// child folders contain items
			const childFolders = foldersByParent.get(folderId) || [];
			for (const childFolder of childFolders) {
				if (folderContainsItems(childFolder.id)) {
					return true;
				}
			}

			return false;
		};

		// Determine which folders need to be expanded
		const foldersToExpand = new Set<number>();
		this.collectionItems.folders.forEach((folder) => {
			if (folderContainsItems(folder.id)) {
				let currentId: number | null = folder.id;
				while (currentId !== null) {
					foldersToExpand.add(currentId);
					const currentFolder = folderMap.get(currentId);
					currentId = currentFolder?.parent_id || null;
				}
			}
		});

		const buildSubtree = (parentId: number | null, level: number): LibraryTreeNode[] => {
			const result: LibraryTreeNode[] = [];

			// Get children at this level
			const childFolders = foldersByParent.get(parentId) || [];
			const childItems = itemsByFolder.get(parentId) || [];

			// Transform to nodes
			const folderNodes = childFolders.map((folder) =>
				new LibraryTreeNode(folder, NodeType.Folder, level, true)
			);

			const itemNodes = childItems.map((item) => {
				const cachedItem = this.itemCacheService.getItem(item.hash);
				const itemToUse = cachedItem || item;
				return new LibraryTreeNode(
					itemToUse,
					NodeType.Item,
					level,
					false,
					false,
					itemToUse.checked || false
				);
			});

			const sortedNodes = this.sortNodes([...folderNodes, ...itemNodes]);
			sortedNodes.forEach((node) => {
				result.push(node);

				if (node.type === NodeType.Folder) {
					const folder = node.data as LibraryFolder;

					// cache direct children
					const directChildFolders = (foldersByParent.get(folder.id) || [])
						.map((f) => new LibraryTreeNode(f, NodeType.Folder, level + 1, true));
					const directChildItems = (itemsByFolder.get(folder.id) || [])
						.map((item) => {
							const cachedItem = this.itemCacheService.getItem(item.hash);
							const itemToUse = cachedItem || item;
							return new LibraryTreeNode(
								itemToUse, NodeType.Item, level + 1, false, false, itemToUse.checked || false
							);
						});

					const directChildren = this.sortNodes([...directChildFolders, ...directChildItems]);
					this.folderCache.set(node.key, { children: directChildren });

					// auto-expand and add children if needed
					if (foldersToExpand.has(folder.id)) {
						this.expandedNodes.add(node.key);
						const children = buildSubtree(folder.id, level + 1);
						result.push(...children);
					}
				}
			});

			return result;
		};

		// start from root
		this.data = buildSubtree(null, 0);
	}

	public isExpanded (node: LibraryTreeNode): boolean {
		return this.expandedNodes.has(node.key);
	}

	public toggle (node: LibraryTreeNode): void {
		if (this.isExpanded(node)) {
			this.handleNodeExpansion(node, false);
		} else {
			this.handleNodeExpansion(node, true);
		}
	}

	public expand (node: LibraryTreeNode): void {
		if (!this.isExpanded(node)) {
			this.handleNodeExpansion(node, true);
		}
	}

	public collapse (node: LibraryTreeNode): void {
		if (this.isExpanded(node)) {
			this.handleNodeExpansion(node, false);
		}
	}

	private handleNodeExpansion (node: LibraryTreeNode, expand: boolean): void {
		const index = this.data.indexOf(node);
		if (index < 0) return;

		if (expand) {
			this.expandedNodes.add(node.key);
			if (node.type === NodeType.Folder) {
				this.loadFolderContents(node, index);
			}
		} else {
			this.expandedNodes.delete(node.key);
			if (node.type === NodeType.Folder) {
				this.collapseFolderContents(node, index);
			}
		}

		this.dataChange.next(this.data);
	}

	private loadFolderContents (node: LibraryTreeNode, index: number): void {
		if (node.isLoading) return;

		// Use cached children for search results
		if (this.isSearchActive()) {
			const cached = this.folderCache.get(node.key);
			if (cached) {
				this.data.splice(index + 1, 0, ...cached.children);
				this.dataChange.next(this.data);
			}
			return;
		}

		// Check cache for normal mode
		const shouldRefresh = this.foldersNeedingRefresh.has(node.key);
		if (!shouldRefresh) {
			const cached = this.folderCache.get(node.key);
			if (cached) {
				this.data.splice(index + 1, 0, ...cached.children);
				this.dataChange.next(this.data);
				return;
			}
		}

		this.foldersNeedingRefresh.delete(node.key);
		node.isLoading = true;
		this.dataChange.next(this.data);

		if (!this.collectionId || !this.contentType) {
			throw new Error('Collection ID and Content Type must be set');
		}

		const existingSubscription = this.loadingSubscriptions.get(node.key);
		if (existingSubscription) {
			existingSubscription.unsubscribe();
		}

		const subscription = this.libraryService.getCollectionItems(
			this.collectionId,
			this.contentType,
			{ folder_id: node.entityId }
		).pipe(finalize(() => {
			node.isLoading = false;
			this.dataChange.next(this.data);
			this.loadingSubscriptions.delete(node.key);
		})).subscribe({
			next: (response) => {
				this.accumulateUserIds(response.user_ids);

				const children = this.transformResponseToSortedNodes(
					response.folders,
					response.items,
					node.level + 1
				);

				this.folderCache.set(node.key, { children });

				const currentIndex = this.data.indexOf(node);
				if (currentIndex >= 0) {
					this.data.splice(currentIndex + 1, 0, ...children);
				}
			},
			error: () => {
				// Handle error if needed
			}
		});

		this.loadingSubscriptions.set(node.key, subscription);
	}

	/**
	 * Properly collapse folder and all nested expanded states
	 */
	private collapseFolderContents (node: LibraryTreeNode, index: number): void {
		// Cancel loading if in progress
		const loadingSubscription = this.loadingSubscriptions.get(node.key);
		if (loadingSubscription) {
			loadingSubscription.unsubscribe();
			this.loadingSubscriptions.delete(node.key);
		}
		node.isLoading = false;

		// Track and remove expanded state for all nested folders
		const descendantsToRemove: string[] = [];
		let count = 0;

		// Find all descendants and collect expanded folder keys
		while (index + 1 + count < this.data.length &&
		this.data[index + 1 + count].level > node.level) {
			const descendant = this.data[index + 1 + count];

			// If it's an expanded folder, mark it for removal from expandedNodes
			if (descendant.type === NodeType.Folder && this.expandedNodes.has(descendant.key)) {
				descendantsToRemove.push(descendant.key);
			}

			count++;
		}

		// Remove expanded state for all nested folders
		descendantsToRemove.forEach((key) => {
			this.expandedNodes.delete(key);
			// Also cancel any loading operations for nested folders
			const sub = this.loadingSubscriptions.get(key);
			if (sub) {
				sub.unsubscribe();
				this.loadingSubscriptions.delete(key);
			}
		});

		// Remove the visual elements
		if (count > 0) {
			this.data.splice(index + 1, count);
			this.dataChange.next(this.data);
		}
	}

	private transformResponseToSortedNodes (
		folders: LibraryFolder[],
		items: LibraryCollectionItem[],
		level: number
	): LibraryTreeNode[] {
		const folderNodes = folders.map((folder) =>
			new LibraryTreeNode(folder, NodeType.Folder, level, true)
		);

		const itemNodes = items.map((item) => {
			const cachedItem = this.itemCacheService.getItem(item.hash);
			const itemToUse = cachedItem || item;
			return new LibraryTreeNode(
				itemToUse,
				NodeType.Item,
				level,
				false,
				false,
				item.checked || false
			);
		});

		return this.sortNodes([...folderNodes, ...itemNodes]);
	}

	private compareNodes (nodeA: LibraryTreeNode, nodeB: LibraryTreeNode): number {
		// Folders always come before items
		if (nodeA.type !== nodeB.type) {
			return nodeA.type === NodeType.Folder ? -1 : 1;
		}

		// get date fields or fallback
		const getDateField = (node: LibraryTreeNode, field: string): Date => {
			const data = node.data;
			if (node.type === NodeType.Folder) {
				const folder = data as LibraryFolder;
				return new Date(folder[field] || folder.created_at);
			} else {
				const item = data as LibraryCollectionItem;
				return new Date(item[field] || item.created_at);
			}
		};

		switch (this.sortBy) {
			case '-name':
				return nodeB.data.name.localeCompare(nodeA.data.name);
			case '-updated_at':
			case '-modified_at':
				return getDateField(nodeB, nodeA.type === NodeType.Folder ? 'updated_at' : 'modified_at').getTime() -
					getDateField(nodeA, nodeA.type === NodeType.Folder ? 'updated_at' : 'modified_at').getTime();
			case '-created_at':
				return getDateField(nodeB, 'created_at').getTime() - getDateField(nodeA, 'created_at').getTime();
			case 'created_at':
				return getDateField(nodeA, 'created_at').getTime() - getDateField(nodeB, 'created_at').getTime();
			case 'name':
			default:
				return nodeA.data.name.localeCompare(nodeB.data.name);
		}
	}

	private sortNodes (nodes: LibraryTreeNode[]): LibraryTreeNode[] {
		const folders = nodes.filter((n) => n.type === NodeType.Folder);
		const items = nodes.filter((n) => n.type === NodeType.Item);

		// Sort separately
		folders.sort((a, b) => this.compareNodes(a, b));
		items.sort((a, b) => this.compareNodes(a, b));

		// folders first, then items
		return [...folders, ...items];
	}

	/**
	 * Re-sort the entire tree maintaining expansion state
	 */
	private resortTree (): void {
		if (this.data.length === 0) return;

		// Group nodes by their parent
		const nodesByParent = new Map<number | null, LibraryTreeNode[]>();
		const existingFolderIds = new Set<number>();

		this.data.forEach((node) => {
			if (node.type === NodeType.Folder) {
				existingFolderIds.add((node.data as LibraryFolder).id);
			}
		});

		this.data.forEach((node) => {
			let parentId = this.getParentId(node);

			// Handle orphans
			if (parentId !== null && !existingFolderIds.has(parentId)) {
				parentId = null;
			}

			if (!nodesByParent.has(parentId)) {
				nodesByParent.set(parentId, []);
			}
			nodesByParent.get(parentId)!.push(node);
		});

		const newData: LibraryTreeNode[] = [];
		const processedNodes = new Set<string>();

		// Recursively rebuild tree with new sort order
		const addNodesRecursively = (parentId: number | null) => {
			const children = nodesByParent.get(parentId) || [];
			const sortedChildren = this.sortNodes(children);

			sortedChildren.forEach((node) => {
				if (processedNodes.has(node.key)) return;

				newData.push(node);
				processedNodes.add(node.key);

				// Add expanded folder's children
				if (node.type === NodeType.Folder && this.expandedNodes.has(node.key)) {
					const folder = node.data as LibraryFolder;
					addNodesRecursively(folder.id);
				}
			});
		};

		addNodesRecursively(null);
		this.data = newData;
	}

	public applyRoot (
		items: LibraryCollectionItemsAndFolders,
		collectionId: number,
		contentType: CONTENT_TYPES,
		preserveState: boolean
	): void {
		const contentChanged = this.collectionId !== collectionId || this.contentType !== contentType;

		if (!preserveState || contentChanged) {
			const savedSearch = this.currentSearch;
			this.clear();
			this.currentSearch = savedSearch;  // restore

			this.collectionItems = items;
			this.collectionId = collectionId;
			this.contentType = contentType;
			this.userIds = items.user_ids;
			this.buildTreeRoot();
			return;
		}

		// Preserve state for same collection/content
		const expanded = new Set(this.expandedNodes);
		this.collectionItems = items;
		this.collectionId = collectionId;
		this.contentType = contentType;
		this.accumulateUserIds(items.user_ids);

		this.rebuildTreePreservingCache(expanded);
	}

	public applyFolder (folderId: number, payload: LibraryCollectionItemsAndFolders): void {
		const parentKey = `folder_${folderId}`;
		const { node: parentNode, index: parentIndex } = this.findNodeWithIndex(parentKey);

		// If folder is not in visible tree, just mark for refresh instead of updating cache
		if (parentIndex < 0) {
			this.foldersNeedingRefresh.add(parentKey);
			this.accumulateUserIds(payload.user_ids);
			return;
		}

		const childLevel = parentNode.level + 1;

		this.accumulateUserIds(payload.user_ids);
		const children = this.transformResponseToSortedNodes(
			payload.folders,
			payload.items,
			childLevel
		);

		this.folderCache.set(parentKey, { children });
		this.foldersNeedingRefresh.delete(parentKey);

		if (this.expandedNodes.has(parentKey)) {
			const start = parentIndex + 1;
			let count = 0;

			while (start + count < this.data.length &&
			this.data[start + count].level > parentNode.level) {
				count++;
			}

			const newVisibleChildren: LibraryTreeNode[] = [];
			this.insertCachedChildrenFlat(newVisibleChildren, { children });

			if (count > 0) {
				this.data.splice(start, count);
			}
			this.data.splice(start, 0, ...newVisibleChildren);
			this.dataChange.next(this.data);
		}
	}

	private insertCachedChildrenFlat (
		targetArray: LibraryTreeNode[],
		cached: FolderCache
	): void {
		for (const child of cached.children) {
			targetArray.push(child);

			if (child.type === NodeType.Folder && this.expandedNodes.has(child.key)) {
				const childCache = this.folderCache.get(child.key);
				if (childCache) {
					this.insertCachedChildrenFlat(targetArray, childCache);
				}
			}
		}
	}

	public isFolderExpanded (folderId: number): boolean {
		return this.expandedNodes.has(`folder_${folderId}`);
	}

	public isItemExpanded (itemHash: string): boolean {
		return this.expandedNodes.has(`item_${itemHash}`);
	}

	public toggleFolder (folderId: number): void {
		const { node: folderNode } = this.findNodeWithIndex(`folder_${folderId}`);
		if (folderNode) {
			this.toggle(folderNode);
		}
	}

	public toggleItem (itemHash: string): void {
		const key = `item_${itemHash}`;
		if (this.expandedNodes.has(key)) {
			this.expandedNodes.delete(key);
		} else {
			this.expandedNodes.add(key);
		}

		this.dataChange.next(this.data);
	}

	public updateItemChecked (itemHash: string, checked: boolean): void {
		const index = this.data.findIndex((n) =>
			n.type === NodeType.Item && (n.data as LibraryCollectionItem).hash === itemHash
		);

		if (index >= 0 && this.data[index].checked !== checked) {
			const oldNode = this.data[index];
			const item = oldNode.data as LibraryCollectionItem;

			const cachedItem = this.itemCacheService.getItem(itemHash);
			const itemToUse = {
				...(cachedItem || item),
				checked
			};

			this.itemCacheService.addItem(itemToUse);

			const newNode = new LibraryTreeNode(
				itemToUse,
				oldNode.type,
				oldNode.level,
				oldNode.expandable,
				oldNode.isLoading,
				checked
			);

			const newData = [...this.data];
			newData[index] = newNode;
			this.data = newData;
		}
	}

	public selectAll (checked: boolean): void {
		this.data.forEach((node) => {
			if (node.type === NodeType.Item) {
				node.checked = checked;
				(node.data as LibraryCollectionItem).checked = checked;
			}
		});

		this.dataChange.next(this.data);
	}

	public getFolderNodeById (folderId: number): LibraryTreeNode | null {
		const { node: folderNode } = this.findNodeWithIndex(`folder_${folderId}`);
		return folderNode || null;
	}

	public clear (): void {
		this.loadingSubscriptions.forEach((sub) => sub.unsubscribe());
		this.loadingSubscriptions.clear();

		this.folderCache.clear();
		this.expandedNodes.clear();
		this.foldersNeedingRefresh.clear();
		this.loadingItemDetails.clear();

		this.collectionItems = null;
		this.collectionId = null;
		this.contentType = CONTENT_TYPES.ALL;
		this.userIds = [];
		this.currentSearch = '';
		this.data = [];
	}

	public collapseAll (): void {
		// Clear expanded state tracking
		this.expandedNodes.clear();

		// Need to rebuild tree showing only root level items
		// since the visual tree still has expanded nodes
		const rootNodes: LibraryTreeNode[] = [];
		for (const node of this.data) {
			if (node.level === 0) {
				rootNodes.push(node);
			}
		}

		this.data = rootNodes;
		this.dataChange.next(this.data);
	}

	public updateFolder (partial: Pick<LibraryFolder, 'id' | 'name' | 'updated_at'>): void {
		const key = `folder_${partial.id}`;

		// 1) Update the visible node (if it's in the flat data)
		const { node: visibleNode } = this.findNodeWithIndex(key);
		if (visibleNode) {
			const data = visibleNode.data as LibraryFolder;
			visibleNode.data = { ...data, ...partial };
		}

		// 2) Update any cached instances of this folder (when its parent is expanded/cached)
		for (const [, cached] of this.folderCache) {
			const child = cached.children.find((n) => n.key === key);
			if (child) {
				const data = child.data as LibraryFolder;
				child.data = { ...data, ...partial };
			}
		}

		// Emit changes if we touched the visible tree
		if (visibleNode) {
			this.dataChange.next(this.data);
		}
	}

	public updateItem (itemHash: string, updatedItem: Partial<LibraryCollectionItem>): void {
		let fullUpdatedItem: LibraryCollectionItem | null = null;

		// Update the visible node in the main tree
		const node = this.data.find((n) =>
			n.type === NodeType.Item && (n.data as LibraryCollectionItem).hash === itemHash
		);

		if (node) {
			const currentData = node.data as LibraryCollectionItem;
			node.data = { ...currentData, ...updatedItem };
			fullUpdatedItem = node.data as LibraryCollectionItem;
		}

		// Update any instances of this item that appear as children in folder caches
		for (const [, cached] of this.folderCache) {
			const childNode = cached.children.find((n) =>
				n.type === NodeType.Item && (n.data as LibraryCollectionItem).hash === itemHash
			);
			if (childNode) {
				const currentData = childNode.data as LibraryCollectionItem;
				childNode.data = { ...currentData, ...updatedItem };
				if (!fullUpdatedItem) {
					fullUpdatedItem = childNode.data as LibraryCollectionItem;
				}
			}
		}

		// Update the item cache service with the complete item
		if (fullUpdatedItem) {
			this.itemCacheService.addItem(fullUpdatedItem);
		}

		// Emit changes if we updated the visible tree
		if (node) {
			this.dataChange.next(this.data);
		}
	}

	public getCheckedItems (): LibraryCollectionItem[] {
		return this.data
			.filter((node) => node.type === NodeType.Item && node.checked)
			.map((node) => node.data as LibraryCollectionItem);
	}

	public getVisibleItems (): LibraryCollectionItem[] {
		return this.data
			.filter((node) => node.type === NodeType.Item)
			.map((node) => node.data as LibraryCollectionItem);
	}

	public getVisibleFolders (): LibraryFolder[] {
		return this.data
			.filter((node) => node.type === NodeType.Folder)
			.map((node) => node.data as LibraryFolder);
	}

	/**
	 * Select/deselect all VISIBLE items only (not cached items in collapsed folders)
	 */
	public selectAllVisible (checked: boolean): void {
		let hasChanges = false;
		const newData = this.data.map((node) => {
			if (node.type === NodeType.Item && node.checked !== checked) {
				hasChanges = true;
				const item = node.data as LibraryCollectionItem;

				const cachedItem = this.itemCacheService.getItem(item.hash);
				const itemToUse = {
					...(cachedItem || item),
					checked
				};

				this.itemCacheService.addItem(itemToUse);

				return new LibraryTreeNode(
					itemToUse,
					node.type,
					node.level,
					node.expandable,
					node.isLoading,
					checked
				);
			}
			return node;
		});

		if (hasChanges) {
			this.data = newData;
		}
	}

	public removeItemsByHash (itemHashes: string[]): void {
		const hashSet = new Set(itemHashes);
		const indicesToRemove: number[] = [];
		const foldersToRefresh = new Set<string>();

		// Find all items to remove and track their folders
		for (let i = this.data.length - 1; i >= 0; i--) {
			const node = this.data[i];
			if (node.type === NodeType.Item) {
				const item = node.data as LibraryCollectionItem;
				if (hashSet.has(item.hash)) {
					indicesToRemove.push(i);
					if (item.library_collection_folder_id) {
						const folderKey = `folder_${item.library_collection_folder_id}`;
						foldersToRefresh.add(folderKey);
					}
				}
			}
		}

		// Remove items from the tree (reverse order to maintain indices)
		for (const index of indicesToRemove) {
			const node = this.data[index];
			const item = node.data as LibraryCollectionItem;

			// Update cache if item was in a folder
			if (item.library_collection_folder_id) {
				const folderKey = `folder_${item.library_collection_folder_id}`;
				this.removeCachedNode(folderKey, node.key);
			}
			this.data.splice(index, 1);
		}

		// Mark all affected folders as needing refresh
		for (const folderKey of foldersToRefresh) {
			this.foldersNeedingRefresh.add(folderKey);
		}

		this.dataChange.next(this.data);
	}

	public moveItems (
		moves: {
			itemId: number;
			fromFolderId: number | null;
			toFolderId: number | null;
		}[]
	): void {
		const moveMap = new Map(moves.map((m) => [m.itemId, m]));
		const itemsToMove: {node: LibraryTreeNode; index: number}[] = [];

		// Find all items to move
		for (let i = 0; i < this.data.length; i++) {
			const node = this.data[i];
			if (node.type === NodeType.Item) {
				const item = node.data as LibraryCollectionItem;
				if (moveMap.has(item.id)) {
					itemsToMove.push({ node, index: i });
				}
			}
		}

		// Sort by index in reverse to remove from end first
		itemsToMove.sort((a, b) => b.index - a.index);

		// Remove all items from their current positions
		for (const { node } of itemsToMove) {
			const item = node.data as LibraryCollectionItem;
			const move = moveMap.get(item.id)!;

			// Update source folder cache
			if (move.fromFolderId !== null) {
				const fromKey = `folder_${move.fromFolderId}`;
				this.foldersNeedingRefresh.add(fromKey);
				this.removeCachedNode(fromKey, node.key);
			}

			// Remove from tree
			const currentIndex = this.data.indexOf(node);
			if (currentIndex >= 0) {
				this.data.splice(currentIndex, 1);
			}
		}

		// Add all items to their new positions
		for (const { node } of itemsToMove) {
			const item = node.data as LibraryCollectionItem;
			const move = moveMap.get(item.id)!;

			// Update item's folder reference
			item.library_collection_folder_id = move.toFolderId;

			if (move.toFolderId === null) {
				// Moving to root
				node.level = 0;
				const insertIndex = this.findInsertionIndex(0, 0, NodeType.Item, node);
				this.data.splice(insertIndex, 0, node);
			} else {
				// Moving to a folder
				const toKey = `folder_${move.toFolderId}`;
				this.foldersNeedingRefresh.add(toKey);

				const { node: toFolderNode, index: toFolderIndex } = this.findNodeWithIndex(toKey);
				if (toFolderNode && toFolderIndex >= 0 && this.expandedNodes.has(toKey)) {
					node.level = toFolderNode.level + 1;

					const insertIndex = this.findInsertionIndex(
						toFolderIndex + 1,
						toFolderNode.level + 1,
						NodeType.Item,
						node,
						toFolderNode.level
					);
					this.data.splice(insertIndex, 0, node);
					this.updateCacheWithNewNode(toKey, node);
				}
			}
		}

		this.dataChange.next(this.data);
	}

	public hasContent (): boolean {
		return this.data.length > 0;
	}

	public clearAllChecked (): void {
		let changed = false;

		for (const node of this.data) {
			if (node.type === NodeType.Item && node.checked) {
				node.checked = false;
				(node.data as LibraryCollectionItem).checked = false;
				changed = true;
			}
		}

		for (const [, cache] of this.folderCache) {
			for (const child of cache.children) {
				if (child.type === NodeType.Item && child.checked) {
					child.checked = false;
					(child.data as LibraryCollectionItem).checked = false;
				}
			}
		}

		if (changed) {
			this.dataChange.next(this.data);
		}
	}

	public setItemDetailsLoading (itemHash: string, isLoading: boolean): void {
		const hadValue = this.loadingItemDetails.has(itemHash);

		if (isLoading) {
			this.loadingItemDetails.add(itemHash);
		} else {
			this.loadingItemDetails.delete(itemHash);
		}

		if (hadValue !== isLoading) {
			this.dataChange.next(this.data);
		}
	}

	public isItemDetailsLoading (itemHash: string): boolean {
		return this.loadingItemDetails.has(itemHash);
	}
}
